import { Formik } from "formik";
import React, { useContext, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { Button,  ThemeProviderV2} from '@blueprint/ui';
import { useModal } from "react-modal-hook";
import * as yup from "yup";

import invoiceApi from "../../../api/invoice";
import { Loader, toast } from "../../../common/components/core";
import { TextareaField } from "../../../common/components/form";
import { useAuthErrorHandlerCreator } from "../../../common/hooks";
import { FormSubmissionHandler, Handler } from "../../../types/common";
import {
    composeErrorHandlers,
    GenericError,
    onStaleStateError,
} from "../../../utils/errorHandling";
import InvoiceContext from "../InvoiceContext";

export type PORejectInvoiceFormValues = {
    comment: string;
};

const schema: yup.SchemaOf<PORejectInvoiceFormValues> = yup.object().shape({
    comment: yup.string().default(""),
});

const usePORejectInvoiceHandler = (invoiceRef: string): Handler => {
    const [isSubmitting, setSubmitting] = useState(false);
    const onAuthError = useAuthErrorHandlerCreator();
    const { mutate } = useContext(InvoiceContext);

    const [show, hide] = useModal(() => {
        const confirm: FormSubmissionHandler<
            PORejectInvoiceFormValues
        > = async (values) => {
            setSubmitting(true);
            try {
                const result = await invoiceApi.poReject(invoiceRef, values);
//                 mutate(result.stateAndRefMap.InvoiceState[0]);
                await mutate();
                toast("Invoice submission rejected");
                hide();
            } catch (e) {
                composeErrorHandlers(
                    onStaleStateError(() => {
                        mutate();
                        hide();
                        toast("Page has been updated by another user.");
                    }),
                    onAuthError,
                )(e as GenericError);
            }
            setSubmitting(false);
        };

        return (
            <ThemeProviderV2>
            <Modal show onHide={hide} backdrop="static" centered>
                <Formik
                    validationSchema={schema}
                    validateOnBlur={false}
                    validateOnChange={false}
                    onSubmit={confirm}
                    initialValues={{
                        comment: "",
                    }}
                >
                    {({ handleSubmit }) => (
                        <Loader overlay isLoading={isSubmitting}>
                            <Form
                                autoComplete="off"
                                noValidate
                                onSubmit={handleSubmit}
                            >
                                <Modal.Header>
                                    <h2 className="mb-0">
                                        Reject Invoice Submission
                                    </h2>
                                </Modal.Header>
                                <Modal.Body>
                                    <p>
                                        Are you sure you want to reject this
                                        invoice submission?
                                    </p>
                                    <TextareaField
                                        name="comment"
                                        label="Comment (Optional)"
                                        maxLength={256}
                                        isAllowed={(formattedValue) => formattedValue.length < 256}
                                    />
                                </Modal.Body>
                                <Modal.Footer className="d-flex justify-content-between px-3">
                                    <Button
                                        variant="light.tertiary"
                                        onClick={hide}
                                    >
                                        Cancel
                                    </Button>
                                    <Button variant="light.primary" type="submit">
                                        Confirm
                                    </Button>
                                </Modal.Footer>
                            </Form>
                        </Loader>
                    )}
                </Formik>
            </Modal>
            </ThemeProviderV2>
        );
    }, [invoiceRef, isSubmitting, mutate, onAuthError]);

    return {
        isSubmitting,
        handler: show,
    };
};

export default usePORejectInvoiceHandler;
